import tailwindTypography from '@tailwindcss/typography'
import tailwindcssScrollBar from 'tailwind-scrollbar'
import type { Config } from 'tailwindcss'
import tailwindcssReactAriaComponents from 'tailwindcss-react-aria-components'
import plugin from 'tailwindcss/plugin'

// The header height is calculated on the first render to prevent layout shifts.
// When the page is loaded, the header height will be calculated and set as CSS variables, and
// the following calculations will be reevaluated.
const HEADER_HEIGHT = '8.5rem' // 136px
const HEADER_HEIGHT_DESKTOP = '9.6875rem' // 155px

const APP_HEADER_HEIGHT = `var(--app-header-height, calc(var(--app-banner-height, 0px) + ${HEADER_HEIGHT}))`
const APP_HEADER_HEIGHT_DESKTOP = `var(--app-header-height-desktop, calc(var(--app-banner-height, 0px) + var(--menu-slider-tabs, 0px) + ${HEADER_HEIGHT_DESKTOP}))`

const screens = {
  /**
   * Screen sizes
   *
   * ⚠️ If adding/removing breakpoints, please update `/public/scripts/responsive.js`
   */
  tablet: '768px' as const,
  desktop: '1024px' as const,
  'desktop-md': '1280px' as const,
  'desktop-lg': '1440px' as const,

  /**
   * Screen resolutions
   */
  hd: {
    raw: '(min-resolution: 1.5x), only screen and (-webkit-min-device-pixel-ratio: 2)',
  },

  // DO NOT USE THIS SCREEN SIZE FOR ANYTHING ELSE THAN THE REFERENCES TABLE
  'forbidden-desktop-ref-table': '1200px' as const, // This is a temporary solution for the references table. Please do not use it.
}

const tailwindConfig = {
  experimental: {
    // See the details from the Tailwind creator about this option:
    // https://github.com/tailwindlabs/tailwindcss/discussions/7317#discussioncomment-2107898
    // optimizeUniversalDefaults: true,
  },
  content: [
    './src/{ui,feature,lib/navigation}/**/*.{tsx,ts,css}',
    './app/**/*.{tsx,ts}',
    './public/**/*.js',
  ],
  theme: {
    colors: {
      primary: 'var(--color-primary)',
      secondary: 'var(--color-secondary)',
      clear: 'rgba(0, 0, 0, 0)',
      current: 'currentColor',
      white: 'var(--color-white)',
      black: 'var(--color-black)',
      role: {
        'admin-bg': 'var(--color-admin-bg)',
        'admin-fg': 'var(--color-admin-fg)',
        'admin-demo-bg': 'var(--color-admin-demo-bg)',
        'admin-demo-fg': 'var(--color-admin-demo-fg)',
        'buyer-bg': 'var(--color-buyer-bg)',
        'buyer-fg': 'var(--color-buyer-fg)',
        'demo-bg': 'var(--color-demo-bg)',
        'demo-fg': 'var(--color-demo-fg)',
        'guest-bg': 'var(--color-guest-bg)',
        'guest-fg': 'var(--color-guest-fg)',
        'manager-bg': 'var(--color-manager-bg)',
        'manager-fg': 'var(--color-manager-fg)',
      },
      success: {
        fg: 'var(--color-success-fg)',
        bg: 'var(--color-success-bg)',
      },
      info: {
        fg: 'var(--color-info-fg)',
        bg: 'var(--color-info-bg)',
      },
      warning: {
        fg: 'var(--color-warning-fg)',
        bg: 'var(--color-warning-bg)',
      },
      error: {
        fg: 'var(--color-error-fg)',
        bg: 'var(--color-error-bg)',
      },
      'click-and-collect': {
        fg: 'var(--color-click-and-collect-fg)',
        bg: 'var(--color-click-and-collect-bg)',
        disabled: 'var(--color-carrot-orange-300)',
      },
      disabled: {
        fg: 'var(--color-disabled-fg)',
        bg: 'var(--color-disabled-bg)',
      },
      grey: {
        50: 'var(--color-grey-50)',
        100: 'var(--color-grey-100)',
        200: 'var(--color-grey-200)',
        300: 'var(--color-grey-300)',
        400: 'var(--color-grey-400)',
        500: 'var(--color-grey-500)',
        600: 'var(--color-grey-600)',
        700: 'var(--color-grey-700)',
        800: 'var(--color-grey-800)',
        900: 'var(--color-grey-900)',
      },
      'apple-green': {
        50: 'var(--color-apple-green-50)',
        100: 'var(--color-apple-green-100)',
        200: 'var(--color-apple-green-200)',
        300: 'var(--color-apple-green-300)',
        400: 'var(--color-apple-green-400)',
        500: 'var(--color-apple-green-500)',
        600: 'var(--color-apple-green-600)',
        700: 'var(--color-apple-green-700)',
        800: 'var(--color-apple-green-800)',
        900: 'var(--color-apple-green-900)',
      },
      'mango-orange': {
        50: 'var(--color-mango-orange-50)',
        100: 'var(--color-mango-orange-100)',
        200: 'var(--color-mango-orange-200)',
        300: 'var(--color-mango-orange-300)',
        400: 'var(--color-mango-orange-400)',
        500: 'var(--color-mango-orange-500)',
        600: 'var(--color-mango-orange-600)',
        700: 'var(--color-mango-orange-700)',
        800: 'var(--color-mango-orange-800)',
        900: 'var(--color-mango-orange-900)',
      },
      'strawberry-red': {
        50: 'var(--color-strawberry-red-50)',
        100: 'var(--color-strawberry-red-100)',
        200: 'var(--color-strawberry-red-200)',
        300: 'var(--color-strawberry-red-300)',
        400: 'var(--color-strawberry-red-400)',
        500: 'var(--color-strawberry-red-500)',
        600: 'var(--color-strawberry-red-600)',
        700: 'var(--color-strawberry-red-700)',
        800: 'var(--color-strawberry-red-800)',
        900: 'var(--color-strawberry-red-900)',
      },
      'topaz-blue': {
        50: 'var(--color-topaz-blue-50)',
        100: 'var(--color-topaz-blue-100)',
        200: 'var(--color-topaz-blue-200)',
        300: 'var(--color-topaz-blue-300)',
        400: 'var(--color-topaz-blue-400)',
        500: 'var(--color-topaz-blue-500)',
        600: 'var(--color-topaz-blue-600)',
        700: 'var(--color-topaz-blue-700)',
        800: 'var(--color-topaz-blue-800)',
        900: 'var(--color-topaz-blue-900)',
      },
      'brand-1': {
        50: 'var(--color-brand-1-50)',
        100: 'var(--color-brand-1-100)',
        200: 'var(--color-brand-1-200)',
        300: 'var(--color-brand-1-300)',
        400: 'var(--color-brand-1-400)',
        500: 'var(--color-brand-1-500)',
        600: 'var(--color-brand-1-600)',
        700: 'var(--color-brand-1-700)',
        800: 'var(--color-brand-1-800)',
        900: 'var(--color-brand-1-900)',
        950: 'var(--color-brand-1-950)',
      },
      'brand-2': {
        50: 'var(--color-brand-2-50)',
        100: 'var(--color-brand-2-100)',
        200: 'var(--color-brand-2-200)',
        300: 'var(--color-brand-2-300)',
        400: 'var(--color-brand-2-400)',
        500: 'var(--color-brand-2-500)',
        600: 'var(--color-brand-2-600)',
        700: 'var(--color-brand-2-700)',
        800: 'var(--color-brand-2-800)',
        900: 'var(--color-brand-2-900)',
        950: 'var(--color-brand-2-950)',
      },
      loyalty: {
        primary: 'var(--color-loyalty-primary)',
        light: 'var(--color-loyalty-light)',
        secondary: 'var(--color-loyalty-secondary)',
      },
    },
    screens,
    spacing: {
      '0': '0',
      '0.5': '0.25rem',
      '1': '0.5rem',
      '2': '1rem',
      '3': '1.5rem',
      '4': '2rem',
      '5': '2.5rem',
      '6': '3rem',
      '7': '3.5rem',
      '8': '4rem',
      '9': '4.5rem',
      '10': '5rem',
      '11': '5.5rem',
      '12': '6rem',
      '13': '6.5rem',
      '14': '7rem',
      '15': '7.5rem',
      '16': '8rem',
      '17': '8.5rem',
      '18': '9rem',
      '19': '9.5rem',
      '20': '10rem',
      '22': '11rem',
      '25': '12.5rem',
    },
    borderRadius: {
      none: '0',
      xs: 'var(--radius-xs)',
      sm: 'var(--radius-sm)',
      md: 'var(--radius-md)',
      lg: 'var(--radius-lg)',
      full: 'var(--radius-full)',
    },
    borderWidth: {
      '0': '0',
      '1.5': '0.09375rem',
      '2': '0.125rem',
      '3': '0.1875rem',
      '4': '0.25rem',
      '5': '0.3125rem',
      px: '0.0625rem',
    },
    fontSize: {
      xs: '0.625rem',
      sm: '0.75rem',
      md: '0.875rem',
      lg: '1rem',
      xl: '1.125rem',
      '2xl': '1.25rem',
      '3xl': '1.5rem',
      '4xl': '1.875rem',
      '5xl': '2.25rem',
      '6xl': '3rem',
    },
    fontWeight: {
      light: '336',
      normal: '394',
      medium: '515',
      semibold: '630',
      bold: '708',
    },
    lineHeight: {
      compact: '1',
      normal: '1.2',
      light: '1.4',
      lighter: '1.5',
      lightest: '1.6',
    },
    translate: {
      px: '1px',
      '1/2': '50%',
      '-1/2': '-50%',
      full: '100%',
      '-full': '-100%',
    },
    keyframes: {
      spin: {
        from: {
          transform: 'translate(var(--tw-translate-x), var(--tw-translate-x)) rotate(0deg)',
        },
        to: {
          transform: 'translate(var(--tw-translate-x), var(--tw-translate-x)) rotate(360deg)',
        },
      },
      'slide-right-in': {
        '0%': {
          transform: 'translateX(100%)',
          opacity: '0',
        },
        '100%': {
          transform: 'translateX(0)',
          opacity: '1',
        },
      },
      'slide-right-out': {
        '0%': {
          transform: 'translateX(0)',
        },
        '100%': {
          transform: 'translateX(100%)',
        },
      },
      'slide-up-in': {
        '0%': {
          transform: 'translateY(100%)',
        },
        '100%': {
          transform: 'translateY(0)',
        },
      },
      'slide-up-out': {
        '0%': {
          transform: 'translateY(0)',
        },
        '100%': {
          transform: 'translateY(100%)',
        },
      },
      'fade-in': {
        '0%': {
          opacity: '0',
        },
        '100%': {
          opacity: '1',
        },
      },
      'fade-out': {
        '0%': {
          opacity: '1',
        },
        '100%': {
          opacity: '0',
        },
      },
      placeholder: {
        '0%': {
          top: '0.000000001px',
        },
        '100%': {
          top: '0px',
        },
      },
      skeleton: {
        '0%': {
          opacity: 'var(--skeleton-opacity-from)',
        },
        '100%': {
          opacity: 'var(--skeleton-opacity-to)',
        },
      },
    },
    animation: {
      spin: 'spin 1s linear infinite',
      'empty-in': 'placeholder 0.5s linear',
      'empty-out': 'placeholder 0.6s linear',
      'slide-right-in': 'slide-right-in 0.5s cubic-bezier(0, 1, 0, 1)',
      'slide-right-out': 'slide-right-out 0.6s cubic-bezier(1, 0, 1, 0)',
      'slide-up-in': 'slide-up-in 0.5s cubic-bezier(0, 1, 0, 1)',
      'slide-up-out': 'slide-up-out 0.6s cubic-bezier(1, 0, 1, 0)',
      'fade-in': 'fade-in 0.5s linear',
      'fade-out': 'fade-out 0.6s linear',
      skeleton: 'skeleton 0.8s ease-in-out alternate infinite',
      none: 'none',
    },
    boxShadow: {
      // TODO: Rename to a xl format, like m, sm, or something else
      header: '0 .25rem 1.5rem -0.2rem rgba(0, 0, 0, 0.15)',
      card: '0 0.5rem 3rem -0.5rem rgba(0, 77, 140, 0.15)',
      callout: '0 0.25rem 1.5rem -0.1875rem rgba(0, 77, 140, 0.15)',
      xs: '0 0.0625rem 0.1875rem 0 rgba(0, 0, 0, 0.1), 0 0.0625rem 0.125rem 0 rgba(0, 0, 0, 0.06)',
      s: '0 0 0.25rem -0.0625rem rgba(32, 38, 51, 0.10) inset, 0 1.5rem 1.5rem -0.75rem rgba(32, 38, 51, 0.04), 0 0.75rem 0.75rem -0.375rem rgba(32, 38, 51, 0.04), 0 0.375rem 0.375rem -0.1875rem rgba(32, 38, 51, 0.04), 0 0.1875rem 0.1875rem -0.09375rem rgba(32, 38, 51, 0.04), 0 0.0625rem 0.0625rem -0.03125rem rgba(32, 38, 51, 0.04)',
      's-top':
        '0rem -1.5rem 1.5rem -0.75rem rgba(32, 38, 51, 0.04), 0rem -0.75rem 0.75rem -0.375rem rgba(32, 38, 51, 0.04), 0rem -0.1875rem 0.1875rem -0.09375rem rgba(32, 38, 51, 0.04), 0rem -0.0625rem 0.0625rem -0.03125rem rgba(32, 38, 51, 0.04)',
      xl: '0 0 1.125rem rgba(0, 0, 0, 0.1)',
      '3xl': '0 .5rem 3rem rgb(0, 0, 0, 0.2)',
      picture:
        '0 2.125rem 2.125rem -0.75rem rgba(32, 38, 51, 0.08),0 0.75rem 0.75rem -0.375rem rgba(32, 38, 51, 0.04),0 0.375rem 0.375rem -0.1875rem rgba(32, 38, 51, 0.04),0 0.1875rem 0.1875rem -0.09375rem rgba(32, 38, 51, 0.04),0 0.0625rem 0.0625rem -0.03125rem rgba(32, 38, 51, 0.04)',
      none: '0 0 #0000',
    },
    zIndex: {
      base: '1',
      'menu-arrows': '5',
      popover: '5',
      'header-overlay': '6',
      header: '7',
      'mobile-menu': '10',
      drawer: '13',
      'search-bar-background': '14',
      'search-bar': '15',
      toast: '20',
      'page-loader': '1000',
    },
    backgroundImage: {
      'gradient-card': 'linear-gradient(180deg, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0.8) 100%)',
      'gradient-card-overlay':
        'linear-gradient(180deg, transparent 15.33%, var(--color-brand-2-950) 72.33%)',
    },
    extend: {
      rotate: {
        '4': '4deg',
        '270': '270deg',
      },
      divideWidth: {
        1: '1px', // divide-x doesn't work
      },
      typography: {
        DEFAULT: {
          css: {
            '--tw-prose-headings': 'var(--color-brand-2-900)',
            '--tw-prose-bold': 'var(--color-brand-2-900)',
          },
        },
      },
      gridTemplateColumns: {
        'auto-fit': 'repeat(auto-fit, minmax(0, 1fr))',
      },
    },
  },
  plugins: [
    tailwindcssReactAriaComponents,
    tailwindcssScrollBar({ nocompatible: true, preferredStrategy: 'pseudoelements' }),
    plugin(function ({ addUtilities }) {
      addUtilities({
        '.no-scrollbar': {
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        },
        '.pt-header': {
          'padding-top': APP_HEADER_HEIGHT,
          [`@media (min-width: ${screens.desktop})`]: {
            'padding-top': APP_HEADER_HEIGHT_DESKTOP,
          },
        },
        '.scroll-pt-header': {
          'scroll-padding-top': `var(--scroll-padding-top, calc(${APP_HEADER_HEIGHT} + 2rem))`,
          [`@media (min-width: ${screens.desktop})`]: {
            'scroll-padding-top': `var(--scroll-padding-top, calc(${APP_HEADER_HEIGHT_DESKTOP} + 2rem))`,
          },
        },
        '.scroll-snap-x': {
          'overscroll-behavior-x': 'contain',
          overflow: 'auto hidden',
          'scroll-snap-type': 'x mandatory',
          '-ms-overflow-style': 'none',
          'scrollbar-width': 'none',
          '-webkit-overflow-scrolling': 'touch',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '& > *': {
            'scroll-snap-align': 'start',
          },
        },
        '.sticky-desktop-element': {
          [`@media (min-width: ${screens.desktop})`]: {
            position: 'sticky',
            top: APP_HEADER_HEIGHT_DESKTOP,
          },
          'body:has(.group\\/drawer.block) &': {
            [`@media (min-width: ${screens.desktop})`]: {
              top: '0',
            },
          },
        },
        '.sticky-mobile-to-header': {
          [`@media (max-width: ${screens.desktop})`]: {
            position: 'sticky',
            top: APP_HEADER_HEIGHT,
            transition: 'top 0.3s',
          },
          'html[data-embed="true"] &': {
            [`@media (max-width: ${screens.desktop})`]: {
              top: '0',
            },
          },
        },
        '.sticky-desktop-element, .sticky-mobile-to-header': {
          'body:has([data-scrolled-beyond]) &': {
            top: '0',
          },
        },
        '.list-style-dash': {
          'li::before': {
            display: 'inline-block',
            content: '"-"',
            'margin-right': '0.3rem',
          },
        },
        '.break-words': {
          'overflow-wrap': 'break-word',
          'word-break': 'break-word',
        },
      })
    }),
    plugin(function ({ addVariant }) {
      addVariant('mouse-only', '@media screen and (pointer: fine)')
      addVariant('touch', '@media (hover: none) and (pointer: coarse)')
      addVariant('embed', 'html[data-embed="true"] &')
    }),
    plugin(function ({ matchUtilities, theme }) {
      matchUtilities(
        {
          'overflow-clip-margin': (value) => ({
            overflowClipMargin: value,
          }),
        },
        {
          values: theme('spacing'),
        },
      )
    }),
    tailwindTypography({
      className: 'wysiwyg',
    }),
  ],
} satisfies Config

export default tailwindConfig
